import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from '@/lib/useAsync';
import { useAppStore } from '@/app/store';
import { ErrorState, Spinner } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { usePortalConfig } from './PortalLayout';

/* The queen's landing view: departments, people and agents, not tooling. */
export function PortalHivePage() {
  const config = usePortalConfig();
  const user = useAppStore((s) => s.user);
  const overview = useAsync(() => api.getPortalOverview(), []);
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState(false);

  if (overview.status === 'loading') return <Spinner label="Waking the hive…" />;
  if (overview.status === 'error') return <ErrorState error={overview.error} onRetry={overview.reload} />;
  const { departments, totals } = overview.data;
  const firstName = (user?.name ?? '').split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening';

  return (
    <>
      <div className="portal-head">
        {config.region && (
          <div className="portal-geo">
            {config.region} <span className="car">▾</span>
          </div>
        )}
        <h1>
          {greeting}, {firstName}
        </h1>
        <p>
          Your hive ran {totals.runsThisWeek} time{totals.runsThisWeek === 1 ? '' : 's'} this week and shipped{' '}
          {totals.deliverablesThisMonth} deliverable{totals.deliverablesThisMonth === 1 ? '' : 's'} this month.
        </p>
      </div>

      <div className="portal-tiles">
        <div className="portal-tile">
          <div className="v">{totals.agents}</div>
          <div className="l">Agents live</div>
          <div className="d">across {totals.departments} department{totals.departments === 1 ? '' : 's'}</div>
        </div>
        <div className="portal-tile">
          <div className="v">{totals.people}</div>
          <div className="l">People in the hive</div>
          <div className="d">unlimited on your plan</div>
        </div>
        <div className="portal-tile">
          <div className="v">{totals.runsThisWeek}</div>
          <div className="l">Runs this week</div>
          <div className="d">scheduled, triggered and on demand</div>
        </div>
        <div className="portal-tile">
          <div className="v">{totals.deliverablesThisMonth}</div>
          <div className="l">Deliverables this month</div>
          <div className="d">reports, briefs, summaries</div>
        </div>
      </div>
      <p className="portal-note">
        <b>Agents are built per function, not per person.</b> A whole team shares one agent; adding people never adds
        agents or cost.
      </p>

      <h3 className="portal-sec">Departments</h3>
      <div className="portal-depts">
        {departments.map((d) => (
          <button key={d.slug} className="portal-dept" onClick={() => navigate(`/portal/d/${d.slug}`)}>
            <div className="name">
              {d.name}
              <span className="portal-count">
                {d.agentCount} agent{d.agentCount === 1 ? '' : 's'}
              </span>
            </div>
            <div className="ppl">{d.peopleCount > 0 ? `${d.peopleCount} people` : 'team being onboarded'}</div>
            <div className="last">
              {d.lastRun ? (
                <>
                  <span style={{ color: 'var(--p-green)' }}>●</span> {d.lastRun.title}
                </>
              ) : (
                <span style={{ color: 'var(--p-ink3)' }}>No runs yet</span>
              )}
            </div>
          </button>
        ))}
        <button
          className="portal-dept req"
          onClick={() => toast.success('Request noted', 'WarriorLab will come back with scope and price.')}
        >
          <div className="plus">+ Request an agent</div>
          <div className="ppl" style={{ marginTop: 6 }}>
            Tell us the function and we scope it with WarriorLab. Priced per agent, live in days.
          </div>
        </button>
      </div>

      <div className="portal-ask">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && question.trim() && setAsked(true)}
          placeholder="Ask the hive anything, like “who did what this week, and where's the friction?”"
        />
        <button className="portal-btn" disabled={!question.trim()} onClick={() => setAsked(true)}>
          Ask
        </button>
      </div>
      {asked && (
        <div className="portal-answer">
          Hive-wide answers arrive with the reporting layer
          <span className="portal-staged">preview</span>
          <br />
          For now, each department's agents answer within their own pages, and every deliverable lists its sources.
        </div>
      )}
      <p className="portal-hint">Answers come from your hive's knowledge only, with the source cited.</p>
    </>
  );
}
